import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Capacitor } from '@capacitor/core';

interface CalendarEventData {
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  location?: string;
  attendees?: { email: string; name: string }[];
}

interface GoogleTokens {
  access_token: string;
  refresh_token?: string;
  expires_at: number;
  token_type: string;
}

export function useGoogleCalendar() {
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // Check connection status on mount
  useEffect(() => {
    checkGoogleConnection();
    
    // Listen for messages from OAuth popup
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'GOOGLE_OAUTH_SUCCESS') {
        setIsConnected(true);
        toast({
          title: "Connexion réussie",
          description: "Votre compte Google Calendar est maintenant connecté !",
        });
      }
    };
    
    // Listen for storage changes (in case tokens are updated in another tab)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'google_calendar_tokens') {
        checkGoogleConnection();
      }
    };
    
    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [toast]);

  const getStoredTokens = (): GoogleTokens | null => {
    try {
      const tokens = localStorage.getItem('google_calendar_tokens');
      return tokens ? JSON.parse(tokens) : null;
    } catch {
      return null;
    }
  };

  const isTokenExpired = (tokens: GoogleTokens): boolean => {
    return Date.now() >= tokens.expires_at - (5 * 60 * 1000); // 5 minutes buffer
  };

  const refreshAccessToken = async (refreshToken: string): Promise<GoogleTokens | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('google-refresh-token', {
        body: { refresh_token: refreshToken }
      });

      if (error || !data?.access_token) {
        console.error('Token refresh failed:', error);
        return null;
      }

      const newTokens: GoogleTokens = {
        access_token: data.access_token,
        refresh_token: refreshToken, // Keep the original refresh token
        expires_at: Date.now() + (data.expires_in * 1000),
        token_type: data.token_type || 'Bearer'
      };

      localStorage.setItem('google_calendar_tokens', JSON.stringify(newTokens));
      return newTokens;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  };

  const getValidToken = async (): Promise<string | null> => {
    let tokens = getStoredTokens();
    
    if (!tokens) {
      return null;
    }

    if (isTokenExpired(tokens) && tokens.refresh_token) {
      tokens = await refreshAccessToken(tokens.refresh_token);
    }

    return tokens?.access_token || null;
  };

  const initGoogleAuth = async () => {
    try {
      // Get the secure auth URL from our edge function
      const returnUrl = Capacitor.isNativePlatform() 
        ? 'kin://oauth-callback' 
        : window.location.origin;
        
      const { data, error } = await supabase.functions.invoke('google-auth-url', {
        body: { returnUrl }
      });
      
      if (error || !data?.success) {
        throw new Error(data?.error || 'Failed to generate auth URL');
      }
      
      if (Capacitor.isNativePlatform()) {
        // Use in-app browser for mobile with dynamic import
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ 
          url: data.authUrl,
          windowName: '_self'
        });
      } else {
        // Use popup for web
        window.open(data.authUrl, '_blank', 'noopener,noreferrer,width=480,height=700');
      }
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      throw error;
    }
  };

  const connectToGoogleCalendar = async () => {
    setIsConnecting(true);
    try {
      // Démarrer le processus d'authentification OAuth
      await initGoogleAuth();
      
      // Arrêter l'état de loading puisque l'auth continue dans une nouvelle fenêtre
      setIsConnecting(false);
      
      toast({
        title: "Fenêtre d'authentification ouverte",
        description: "Veuillez compléter l'authentification dans la nouvelle fenêtre.",
      });
    } catch (error: any) {
      console.error('Erreur connexion Google Calendar:', error);
      toast({
        title: "Erreur de connexion",
        description: "Impossible de se connecter à Google Calendar. Veuillez réessayer.",
        variant: "destructive",
      });
      setIsConnecting(false);
    }
  };

  const syncEventToGoogleCalendar = async (eventData: CalendarEventData) => {
    console.log('🔄 Starting Google Calendar sync...', { eventData, isConnected });
    
    try {
      // Get valid access token (with refresh if needed)
      const accessToken = await getValidToken();
      console.log('🔑 Access token obtained:', !!accessToken);
      
      if (!accessToken) {
        console.error('❌ No access token available for Google Calendar sync');
        toast({
          title: "Connexion requise",
          description: "Vous devez d'abord vous connecter à Google Calendar.",
          variant: "destructive",
        });
        return { success: false, requiresAuth: true };
      }

      // Call edge function to create the event
      console.log('📞 Calling sync-google-calendar edge function...');
      const { data, error } = await supabase.functions.invoke('sync-google-calendar', {
        body: {
          eventData,
          accessToken
        }
      });
      console.log('📞 Edge function response:', { data, error });

      if (error) {
        console.error('Erreur lors de la synchronisation:', error);
        
        if (error.message?.includes('requiresAuth') || error.message?.includes('401')) {
          // Token expired or invalid, clear stored tokens
          localStorage.removeItem('google_calendar_tokens');
          setIsConnected(false);
          
          toast({
            title: "Reconnexion requise",
            description: "Votre session Google Calendar a expiré. Veuillez vous reconnecter.",
            variant: "destructive",
          });
          
          return { success: false, requiresAuth: true };
        }
        
        throw error;
      }

      if (data?.success) {
        toast({
          title: "Événement synchronisé",
          description: "L'événement a été ajouté à votre Google Calendar.",
        });
        
        return { 
          success: true, 
          googleEventId: data.googleEventId,
          googleEventUrl: data.googleEventUrl 
        };
      } else {
        throw new Error(data?.error || 'Erreur inconnue');
      }

    } catch (error: any) {
      console.error('Erreur synchronisation Google Calendar:', error);
      toast({
        title: "Erreur de synchronisation",
        description: error.message || "Impossible de synchroniser avec Google Calendar.",
        variant: "destructive",
      });
      
      return { success: false, error: error.message };
    }
  };

  // Check if user is connected to Google Calendar
  const checkGoogleConnection = () => {
    const tokens = getStoredTokens();
    const connected = !!tokens?.access_token;
    setIsConnected(connected);
    return connected;
  };

  const disconnectGoogleCalendar = () => {
    localStorage.removeItem('google_calendar_tokens');
    setIsConnected(false);
    toast({
      title: "Déconnecté",
      description: "Votre compte Google Calendar a été déconnecté.",
    });
  };

  return {
    isConnecting,
    isConnected,
    connectToGoogleCalendar,
    syncEventToGoogleCalendar,
    checkGoogleConnection,
    disconnectGoogleCalendar,
  };
}